// pages/ServicioCocina.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Badge,
  Statistic,
  Card,
  Divider,
  DatePicker,
  InputNumber,
  Progress,
  Popover,
  Collapse
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  ExportOutlined,
  CalendarOutlined,
  FilterOutlined,
  DownloadOutlined,
  AppstoreOutlined,
  CoffeeOutlined,
  ShoppingOutlined,  
  TeamOutlined,
  PieChartOutlined,
  BarChartOutlined,
  LineChartOutlined,
  InfoCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;

const ServicioCocina = () => {
  // Estados principales
  const [datos, setDatos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fechas, setFechas] = useState({
    desde: dayjs().format('DD/MM/YYYY'),
    hasta: dayjs().add(7, 'day').format('DD/MM/YYYY')
  });
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    searchText: '',
    observacionesFilter: ''
  });
  
  // Estados para paginación
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} días`,
    pageSizeOptions: ['10', '20', '50', '100']
  });
  
  // Estados para ordenación
  const [sortedInfo, setSortedInfo] = useState({});
  
  // Estadísticas y totales
  const [totales, setTotales] = useState({
    TotalAlmuerzos: 0,
    Desayuno: 0,
    Almuerzo: 0,
    Cena: 0,
    TotalD: 0,
    TotalA: 0,
    TotalC: 0,
    TodoIncluido: 0
  });
  
  const [resumen, setResumen] = useState({
    Desayuno: 0,
    Almuerzo: 0,
    Cena: 0,
    TodoIncluido: 0,
    TotalD: 0,
    TotalA: 0,
    TotalC: 0,
    Dias: 0,
    PromedioDesayuno: 0,
    PromedioAlmuerzo: 0,
    PromedioCena: 0,
    PromedioTodoIncluido: 0
  });

  // Configuración de columnas principales
// Reemplazar las columnasPrincipales por esta versión corregida
const columnsPrincipales = [
  {
    title: 'Fecha',
    dataIndex: 'Fecha',
    key: 'Fecha',
    width: 110,
    sorter: (a, b) => new Date(a.Fecha) - new Date(b.Fecha),
    sortOrder: sortedInfo.columnKey === 'Fecha' && sortedInfo.order,
    render: (fecha) => {
      if (!fecha) return '-';
      const date = new Date(fecha);
      return (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {date.toLocaleDateString('es-ES', { weekday: 'short' })}
          </div>
          <div style={{ fontSize: '12px' }}>
            {date.toLocaleDateString('es-ES')}
          </div>
        </div>
      );
    },
    fixed: 'left',
  },
  {
    title: 'Desayuno',
    dataIndex: 'Desayuno',
    key: 'Desayuno',
    width: 90,
    sorter: (a, b) => a.Desayuno - b.Desayuno,
    sortOrder: sortedInfo.columnKey === 'Desayuno' && sortedInfo.order,
    render: (valor) => (
      <Tag color="orange" style={{ fontWeight: 'bold', minWidth: '50px', textAlign: 'center' }}>
        {valor}
      </Tag>
    ),
  },
  {
    title: 'Picnic D',
    dataIndex: 'PicnicD',
    key: 'PicnicD',
    width: 85,
    sorter: (a, b) => a.PicnicD - b.PicnicD,
    sortOrder: sortedInfo.columnKey === 'PicnicD' && sortedInfo.order,
    render: (valor) => (
      <Badge 
        count={valor} 
        style={{ backgroundColor: '#fa8c16' }}
        showZero
      />
    ),
  },
  {
    title: 'Total D',
    dataIndex: 'TotalD',
    key: 'TotalD',
    width: 85,
    sorter: (a, b) => a.TotalD - b.TotalD,
    sortOrder: sortedInfo.columnKey === 'TotalD' && sortedInfo.order,
    render: (valor) => (
      <div style={{ fontWeight: 'bold', color: '#d46b08', textAlign: 'center' }}>
        {valor}
      </div>
    ),
  },
  {
    title: 'Almuerzo',
    dataIndex: 'Almuerzo',
    key: 'Almuerzo',
    width: 90,
    sorter: (a, b) => a.Almuerzo - b.Almuerzo,
    sortOrder: sortedInfo.columnKey === 'Almuerzo' && sortedInfo.order,
    render: (valor) => (
      <Tag color="green" style={{ fontWeight: 'bold', minWidth: '50px', textAlign: 'center' }}>
        {valor}
      </Tag>
    ),
  },
  {
    title: 'Picnic A',
    dataIndex: 'PicnicA',
    key: 'PicnicA',
    width: 85,
    sorter: (a, b) => a.PicnicA - b.PicnicA,
    sortOrder: sortedInfo.columnKey === 'PicnicA' && sortedInfo.order,
    render: (valor) => (
      <Badge 
        count={valor} 
        style={{ backgroundColor: '#73d13d' }}
        showZero
      />
    ),
  },
  {
    title: 'Total A',
    dataIndex: 'TotalA',
    key: 'TotalA',
    width: 85,
    sorter: (a, b) => a.TotalA - b.TotalA,
    sortOrder: sortedInfo.columnKey === 'TotalA' && sortedInfo.order,
    render: (valor) => (
      <div style={{ fontWeight: 'bold', color: '#389e0d', textAlign: 'center' }}>
        {valor}
      </div>
    ),
  },
  {
    title: 'Cena',
    dataIndex: 'Cena',
    key: 'Cena',
    width: 90,
    sorter: (a, b) => a.Cena - b.Cena,
    sortOrder: sortedInfo.columnKey === 'Cena' && sortedInfo.order,
    render: (valor) => (
      <Tag color="blue" style={{ fontWeight: 'bold', minWidth: '50px', textAlign: 'center' }}>
        {valor}
      </Tag>
    ),
  },
  {
    title: 'Picnic C',
    dataIndex: 'PicnicC',
    key: 'PicnicC',
    width: 85,
    sorter: (a, b) => a.PicnicC - b.PicnicC,
    sortOrder: sortedInfo.columnKey === 'PicnicC' && sortedInfo.order,
    render: (valor) => (
      <Badge 
        count={valor} 
        style={{ backgroundColor: '#597ef7' }}
        showZero
      />
    ),
  },
  {
    title: 'Total C',
    dataIndex: 'TotalC',
    key: 'TotalC',
    width: 85,
    sorter: (a, b) => a.TotalC - b.TotalC,
    sortOrder: sortedInfo.columnKey === 'TotalC' && sortedInfo.order,
    render: (valor) => (
      <div style={{ fontWeight: 'bold', color: '#096dd9', textAlign: 'center' }}>
        {valor}
      </div>
    ),
  },
  {
    title: 'Todo Incluido',
    dataIndex: 'TodoIncluido',
    key: 'TodoIncluido',
    width: 110,
    sorter: (a, b) => a.TodoIncluido - b.TodoIncluido,
    sortOrder: sortedInfo.columnKey === 'TodoIncluido' && sortedInfo.order,
    render: (valor) => {
      // Mostrar el valor exacto, no 99+
      return (
        <div style={{ 
          fontWeight: 'bold', 
          color: '#722ed1',
          textAlign: 'center',
          padding: '4px 8px',
          backgroundColor: '#f9f0ff',
          borderRadius: '4px',
          border: '1px solid #d3adf7'
        }}>
          {valor}
        </div>
      );
    },
  },
  {
    title: 'Total Servicios',
    key: 'TotalServicios',
    width: 120,
    render: (_, record) => {
      const total = record.TotalD + record.TotalA + record.TotalC;
      return (
        <div style={{ 
          fontWeight: 'bold', 
          textAlign: 'center',
          padding: '4px',
          backgroundColor: total > 0 ? '#f6ffed' : '#fff',
          border: '1px solid #b7eb8f',
          borderRadius: '4px'
        }}>
          {total}
        </div>
      );
    },
  },
  {
    title: 'Observaciones',
    dataIndex: 'Observaciones',
    key: 'Observaciones',
    width: 180,
    render: (obs) => (
      <div style={{ maxWidth: 180, fontSize: '12px' }}>
        {obs || '-'}
      </div>
    ),
  },
  {
    title: 'Acciones',
    key: 'acciones',
    width: 80,
    fixed: 'right',
    render: (_, record) => (
      <Tooltip title="Ver detalles">
        <Button
          type="default"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => handleVerDetalles(record)}
        />
      </Tooltip>
    ),
  },
];

  // Columnas extendidas para el modal de detalles
  const columnsDetalles = [
    {
      title: 'Fecha',
      dataIndex: 'Fecha',
      key: 'Fecha',
      width: 100,
      render: (fecha) => fecha ? new Date(fecha).toLocaleDateString('es-ES') : '-',
    },
    {
      title: 'Desayuno',
      dataIndex: 'Desayuno',
      key: 'Desayuno',
      width: 90,
    },
    {
      title: 'Almuerzo',
      dataIndex: 'Almuerzo',
      key: 'Almuerzo',
      width: 90,
    },
    {
      title: 'Cena',
      dataIndex: 'Cena',
      key: 'Cena',
      width: 90,
    },
    {
      title: 'Picnic D',
      dataIndex: 'PicnicD',
      key: 'PicnicD',
      width: 90,
    },
    {
      title: 'Picnic A',
      dataIndex: 'PicnicA',
      key: 'PicnicA',
      width: 90,
    },
    {
      title: 'Picnic C',
      dataIndex: 'PicnicC',
      key: 'PicnicC',
      width: 90,
    },
    {
      title: 'Total D',
      dataIndex: 'TotalD',
      key: 'TotalD',
      width: 90,
      render: (valor) => <Text strong style={{ color: '#d46b08' }}>{valor}</Text>,
    },
    {
      title: 'Total A',
      dataIndex: 'TotalA',
      key: 'TotalA',
      width: 90,
      render: (valor) => <Text strong style={{ color: '#389e0d' }}>{valor}</Text>,
    },
    {
      title: 'Total C',
      dataIndex: 'TotalC',
      key: 'TotalC',
      width: 90,
      render: (valor) => <Text strong style={{ color: '#096dd9' }}>{valor}</Text>,
    },
    {
      title: 'Todo Incluido',
      dataIndex: 'TodoIncluido',
      key: 'TodoIncluido',
      width: 110,
    },
    {
      title: 'Total PC',
      dataIndex: 'TotalPC',
      key: 'TotalPC',
      width: 90,
    },
    {
      title: 'Total MP',
      dataIndex: 'TotalMP',
      key: 'TotalMP',
      width: 90,
    },
    {
      title: 'Total HD',
      dataIndex: 'TotalHD',
      key: 'TotalHD',
      width: 90,
    },
    {
      title: 'Bebida Inc.',
      dataIndex: 'BebidaIncluido',
      key: 'BebidaIncluido',
      width: 100,
    },
    {
      title: 'Free Drinks',
      dataIndex: 'FreeDrinks',
      key: 'FreeDrinks',
      width: 100,
    },
    {
      title: 'Extras',
      dataIndex: 'Extras',
      key: 'Extras',
      width: 90,
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchDatos();
    cargarResumen();
  }, [fechas, pagination.current, pagination.pageSize]);

  const fetchDatos = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const queryParams = {
        fechaDesde: fechas.desde,
        fechaHasta: fechas.hasta,
        observacionesFilter: filtros.observacionesFilter || undefined,
        search: filtros.searchText || undefined,
        sortField: params.sortField || sortedInfo.field || 'Fecha',
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC'),
        page: params.current || pagination.current,
        pageSize: params.pageSize || pagination.pageSize
      };
      
      const response = await axios.get(`${BASE_URL}/api/serviciococina`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        Fecha: item.Fecha || null,
        Observaciones: item.Observaciones || '',
        // Asegurar valores numéricos
        Desayuno: Number(item.Desayuno) || 0,
        Almuerzo: Number(item.Almuerzo) || 0,
        Cena: Number(item.Cena) || 0,
        TotalD: Number(item.TotalD) || 0,
        TotalA: Number(item.TotalA) || 0,
        TotalC: Number(item.TotalC) || 0,
        TodoIncluido: Number(item.TodoIncluido) || 0
      }));
      
      setDatos(cleanedData);
      
      // Actualizar totales
      if (response.data.totales) {
        setTotales(response.data.totales);
      }
      
      // Actualizar paginación
      if (response.data.pagination) {
        setPagination({
          ...pagination,
          current: response.data.pagination.currentPage || 1,
          pageSize: response.data.pagination.pageSize || pagination.pageSize,
          total: response.data.pagination.totalRecords || 0
        });
      }
      
    } catch (error) {
      console.error('❌ Error cargando servicio de cocina:', error);
      message.error('Error al cargar el servicio de cocina');
      setDatos([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const cargarResumen = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/serviciococina/resumen`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fechaDesde: fechas.desde,
          fechaHasta: fechas.hasta
        }
      });
      
      if (response.data.success) {
        setResumen(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error cargando resumen:', error);
    }
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    setSortedInfo(sorter);
    
    const params = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      sortField: sorter.field,
      sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
    };
    
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
    
    fetchDatos(params);
  };

  const handleFiltrar = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchDatos({ current: 1 });
    cargarResumen();
  };

  const handleResetFiltros = () => {
    setFiltros({
      searchText: '',
      observacionesFilter: ''
    });
    setSortedInfo({});
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchDatos({ current: 1 });
    cargarResumen();
  };

  const handleVerDetalles = (record) => {
    Modal.info({
      title: `Detalles Servicio Cocina - ${new Date(record.Fecha).toLocaleDateString('es-ES')}`,
      width: 1200,
      icon: <EyeOutlined />,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card size="small" title="Resumen del día">
                <Row gutter={[16, 16]}>
                  <Col span={6}>
                    <Statistic 
                      title="Desayuno" 
                      value={record.Desayuno}
                      prefix={<CoffeeOutlined />}
                      valueStyle={{ color: '#d46b08' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="Almuerzo" 
                      value={record.Almuerzo}
                      prefix={<ShoppingOutlined />}
                      valueStyle={{ color: '#389e0d' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="Cena" 
                      value={record.Cena}
                      prefix={<ShoppingOutlined />}
                      valueStyle={{ color: '#096dd9' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="Todo Incluido" 
                      value={record.TodoIncluido}
                      prefix={<TeamOutlined />}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
            
            <Col span={24}>
              <Table
                columns={columnsDetalles}
                dataSource={[record]}
                rowKey="Fecha"
                pagination={false}
                size="small"
                bordered
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={4}>
                        <Text strong>Totales con Picnic:</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <Text strong style={{ color: '#d46b08' }}>
                          {record.TotalD}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>
                        <Text strong style={{ color: '#389e0d' }}>
                          {record.TotalA}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        <Text strong style={{ color: '#096dd9' }}>
                          {record.TotalC}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} colSpan={10}>
                        <Text strong>Servicios Totales: {record.TotalD + record.TotalA + record.TotalC}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Col>
            
            {record.Observaciones && (
              <Col span={24}>
                <Card size="small" title="Observaciones">
                  <div style={{ whiteSpace: 'pre-wrap', padding: '8px' }}>
                    {record.Observaciones}
                  </div>
                </Card>
              </Col>
            )}
            
            <Col span={24}>
              <Collapse ghost>
                <Panel header="Información Adicional" key="1">
                  <Row gutter={[16, 16]}>
                    <Col span={6}>
                      <Card size="small">
                        <Statistic 
                          title="Total PC" 
                          value={record.TotalPC}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small">
                        <Statistic 
                          title="Total MP" 
                          value={record.TotalMP}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small">
                        <Statistic 
                          title="Total HD" 
                          value={record.TotalHD}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small">
                        <Statistic 
                          title="Bebida Incluido" 
                          value={record.BebidaIncluido}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small">
                        <Statistic 
                          title="Free Drinks" 
                          value={record.FreeDrinks}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small">
                        <Statistic 
                          title="Extras" 
                          value={record.Extras}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small">
                        <Statistic 
                          title="Gratis" 
                          value={record.Gratis}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Card>
                    </Col>
                  </Row>
                </Panel>
              </Collapse>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleExportar = async (formato = 'excel') => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${BASE_URL}/api/serviciococina/exportar`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fechaDesde: fechas.desde,
          fechaHasta: fechas.hasta,
          formato
        }
      });
      
      if (response.data.success) {
        message.success(`Exportación (${formato.toUpperCase()}) lista para ${response.data.data.length} días`);
        
        // Simulación de descarga
        const dataStr = JSON.stringify(response.data.data, null, 2);
        const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
        const exportFileDefaultName = `servicio_cocina_${fechas.desde}_${fechas.hasta}.json`;
        
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
      }
    } catch (error) {
      console.error('❌ Error exportando:', error);
      message.error('Error al exportar los datos');
    }
  };

  const handleFechasChange = (dates, dateStrings) => {
    if (dates && dates[0] && dates[1]) {
      setFechas({
        desde: dateStrings[0],
        hasta: dateStrings[1]
      });
    }
  };

  // Componente de filtros
  const filtrosComponent = (
    <Card 
      size="small" 
      title="Filtros" 
      extra={<FilterOutlined />}
      style={{ marginBottom: 16 }}
    >
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={24} md={8} lg={6}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Fechas (DD/MM/YYYY):</Text>
            <RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={[dayjs(fechas.desde, 'DD/MM/YYYY'), dayjs(fechas.hasta, 'DD/MM/YYYY')]}
              onChange={handleFechasChange}
            />
          </div>
        </Col>
        
        <Col xs={24} sm={24} md={8} lg={6}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Búsqueda:</Text>
            <Input
              placeholder="Buscar por fecha u observaciones..."
              value={filtros.searchText}
              onChange={(e) => setFiltros({ ...filtros, searchText: e.target.value })}
              prefix={<SearchOutlined />}
              allowClear
              onPressEnter={handleFiltrar}
            />
          </div>
        </Col>
        
        <Col xs={24} sm={24} md={8} lg={6}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Filtrar por Observaciones:</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Seleccionar..."
              value={filtros.observacionesFilter || undefined}
              onChange={(value) => setFiltros({ ...filtros, observacionesFilter: value })}
              allowClear
            >
              <Option value="">Todas</Option>
              <Option value="ESPECIAL">Especiales</Option>
              <Option value="FESTIVO">Festivos</Option>
              <Option value="EVENTO">Eventos</Option>
            </Select>
          </div>
        </Col>
        
        <Col xs={24} sm={24} md={8} lg={6}>
          <Space>
            <Button
              type="primary"
              icon={<FilterOutlined />}
              onClick={handleFiltrar}
            >
              Aplicar Filtros
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleResetFiltros}
            >
              Restablecer
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  // Estadísticas principales
  // Añadir nuevas estadísticas para Picnic
const estadisticasComponent = (
  <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
    <Col xs={24} sm={12} md={6} lg={3}>
      <Statistic 
        title="Desayuno" 
        value={totales.Desayuno}
        prefix={<CoffeeOutlined />}
        valueStyle={{ color: '#d46b08' }}
      />
    </Col>
    <Col xs={24} sm={12} md={6} lg={3}>
      <Statistic 
        title="Picnic D" 
        value={totales.PicnicD}
        valueStyle={{ color: '#fa8c16' }}
        suffix={<Text type="secondary" style={{ fontSize: '12px' }}>+{totales.PicnicD}</Text>}
      />
    </Col>
    <Col xs={24} sm={12} md={6} lg={3}>
      <Statistic 
        title="Almuerzo" 
        value={totales.Almuerzo}
        prefix={<ShoppingOutlined />}
        valueStyle={{ color: '#389e0d' }}
      />
    </Col>
    <Col xs={24} sm={12} md={6} lg={3}>
      <Statistic 
        title="Picnic A" 
        value={totales.PicnicA}
        valueStyle={{ color: '#73d13d' }}
        suffix={<Text type="secondary" style={{ fontSize: '12px' }}>+{totales.PicnicA}</Text>}
      />
    </Col>
    <Col xs={24} sm={12} md={6} lg={3}>
      <Statistic 
        title="Cena" 
        value={totales.Cena}
        prefix={<AppstoreOutlined />}
        valueStyle={{ color: '#096dd9' }}
      />
    </Col>
    <Col xs={24} sm={12} md={6} lg={3}>
      <Statistic 
        title="Picnic C" 
        value={totales.PicnicC}
        valueStyle={{ color: '#597ef7' }}
        suffix={<Text type="secondary" style={{ fontSize: '12px' }}>+{totales.PicnicC}</Text>}
      />
    </Col>
    <Col xs={24} sm={12} md={6} lg={3}>
      <Statistic 
        title="Todo Incluido" 
        value={totales.TodoIncluido}
        prefix={<TeamOutlined />}
        valueStyle={{ color: '#722ed1' }}
      />
    </Col>
    <Col xs={24} sm={12} md={6} lg={3}>
      <Statistic 
        title="Total Servicios" 
        value={totales.TotalD + totales.TotalA + totales.TotalC}
        prefix={<AppstoreOutlined />}
        valueStyle={{ color: '#1890ff' }}
        suffix={`Días: ${resumen.Dias}`}
      />
    </Col>
  </Row>
);

  // Resumen detallado
  const resumenComponent = (
    <Card 
      size="small" 
      title="Resumen por Categorías" 
      extra={<PieChartOutlined />}
      style={{ marginBottom: 16 }}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Table
            columns={[
              {
                title: 'Categoría',
                dataIndex: 'categoria',
                key: 'categoria',
                width: 150,
              },
              {
                title: 'Desayuno',
                dataIndex: 'desayuno',
                key: 'desayuno',
                width: 100,
                render: (valor) => <Text strong style={{ color: '#d46b08' }}>{valor}</Text>,
              },
              {
                title: 'Almuerzo',
                dataIndex: 'almuerzo',
                key: 'almuerzo',
                width: 100,
                render: (valor) => <Text strong style={{ color: '#389e0d' }}>{valor}</Text>,
              },
              {
                title: 'Cena',
                dataIndex: 'cena',
                key: 'cena',
                width: 100,
                render: (valor) => <Text strong style={{ color: '#096dd9' }}>{valor}</Text>,
              },
              {
                title: 'Todo Incluido',
                dataIndex: 'todoIncluido',
                key: 'todoIncluido',
                width: 120,
                render: (valor) => <Text strong style={{ color: '#722ed1' }}>{valor}</Text>,
              },
              {
                title: 'Total D',
                dataIndex: 'totalD',
                key: 'totalD',
                width: 90,
              },
              {
                title: 'Total A',
                dataIndex: 'totalA',
                key: 'totalA',
                width: 90,
              },
              {
                title: 'Total C',
                dataIndex: 'totalC',
                key: 'totalC',
                width: 90,
              },
            ]}
            dataSource={[
              {
                key: '1',
                categoria: 'Totales Período',
                desayuno: totales.Desayuno,
                almuerzo: totales.Almuerzo,
                cena: totales.Cena,
                todoIncluido: totales.TodoIncluido,
                totalD: totales.TotalD,
                totalA: totales.TotalA,
                totalC: totales.TotalC,
              },
              {
                key: '2',
                categoria: 'Promedio por Día',
                desayuno: resumen.PromedioDesayuno,
                almuerzo: resumen.PromedioAlmuerzo,
                cena: resumen.PromedioCena,
                todoIncluido: resumen.PromedioTodoIncluido,
                totalD: (totales.TotalD / resumen.Dias).toFixed(1),
                totalA: (totales.TotalA / resumen.Dias).toFixed(1),
                totalC: (totales.TotalC / resumen.Dias).toFixed(1),
              }
            ]}
            pagination={false}
            size="small"
            bordered
          />
        </Col>
      </Row>
    </Card>
  );

  return (
    <>
      <PageContainer
        title="Servicio de Cocina"
        subtitle={`Control de servicios del ${fechas.desde} al ${fechas.hasta}`}
        icon={<CoffeeOutlined />}
        loading={loading}
        onRefresh={() => {
          fetchDatos();
          cargarResumen();
        }}
        extra={
          <Space>
            <Popover
              title="Exportar datos"
              content={
                <Space direction="vertical">
                  <Button 
                    icon={<FileExcelOutlined />} 
                    onClick={() => handleExportar('excel')}
                    block
                  >
                    Exportar a Excel
                  </Button>
                  <Button 
                    icon={<FilePdfOutlined />} 
                    onClick={() => handleExportar('pdf')}
                    block
                  >
                    Exportar a PDF
                  </Button>
                </Space>
              }
              trigger="click"
            >
              <Button icon={<ExportOutlined />}>
                Exportar
              </Button>
            </Popover>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{pagination.total.toLocaleString()}</strong> días
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Filtros */}
        {filtrosComponent}
        
        {/* Estadísticas */}
        {estadisticasComponent}
        
        {/* Resumen */}
        {resumenComponent}
        
        {/* Tabla principal */}
        <Table
          columns={columnsPrincipales}
          dataSource={datos}
          rowKey="Fecha"
          loading={loading}
          onChange={handleTableChange}
          pagination={pagination}
          scroll={{ x: 1200, y: 600 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: 'No se encontraron datos para el período seleccionado'
          }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                <Table.Summary.Cell index={0}>
                  <Text strong>TOTALES</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Text strong style={{ color: '#d46b08' }}>{totales.Desayuno}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <Text strong style={{ color: '#389e0d' }}>{totales.Almuerzo}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <Text strong style={{ color: '#096dd9' }}>{totales.Cena}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <Text strong style={{ color: '#d46b08' }}>{totales.TotalD}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5}>
                  <Text strong style={{ color: '#389e0d' }}>{totales.TotalA}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6}>
                  <Text strong style={{ color: '#096dd9' }}>{totales.TotalC}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7}>
                  <Text strong style={{ color: '#722ed1' }}>{totales.TodoIncluido}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} colSpan={2}>
                  <Text type="secondary">Total servicios: {totales.TotalD + totales.TotalA + totales.TotalC}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </PageContainer>
    </>
  );
};

export default ServicioCocina;